import React, { createContext, useContext, useEffect, useState } from 'react';
import { login } from '../services/auth';
import { getSession } from '../services/service';

export const AuthContext = createContext(undefined);

const getUserData = () => {
  try {
    const stored = localStorage.getItem('userData');
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(undefined);
  const [userData, setUserData] = useState(undefined);

  useEffect(() => {
    if (!session) {
      setSession(getSession());
    }
    // Rehydrate userData from localStorage on refresh
    setUserData(getUserData());
  }, []);

  const handleLogin = async (username, password) => {
    try {
      const s = await login(username, password);

      if (s) {
        setSession(s.session);
        localStorage.setItem('session', JSON.stringify(s.session));
        localStorage.setItem('userRole', 'user');

        // Persist userData so it survives refresh
        setUserData(s.userData);
        localStorage.setItem('userData', JSON.stringify(s.userData));

        return s;
      } else {
        setSession(undefined);
        setUserData(undefined);
        return undefined;
      }
    } catch (e) {
      setSession(undefined);
      setUserData(undefined);
      throw e;
    }
  };

  const handleLogout = () => {
    setSession(undefined);
    setUserData(undefined);
    localStorage.removeItem('session');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
  };

  // Patches userData locally (and in localStorage) without a re-login or a
  // fresh fetch. userData/session here are managed by this context directly
  // (not through react-query), so there's no query cache to invalidate —
  // this is the equivalent of that for context-held state. Used e.g. after
  // a successful email verification to flip `isVerified` to true so the
  // verification modal stops re-opening.
  const updateUserData = (partialUserData) => {
    setUserData((prev) => {
      const next = { ...(prev || {}), ...partialUserData };
      localStorage.setItem('userData', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    session,
    userData,
    login: handleLogin,
    logout: handleLogout,
    updateUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
